import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, FormHelperText, Chip, Typography, Grid, Divider, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { User } from '../../types/auth';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface UserModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    initialData?: User;
    isEditing: boolean;
}

interface Organization {
    _id: string;
    name: string;
}

const UserModal: React.FC<UserModalProps> = ({ open, onClose, onSave, initialData, isEditing }) => {
    const { user: currentUser } = useAuth();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('learner');
    const [organizationId, setOrganizationId] = useState('');
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [questionScope, setQuestionScope] = useState<'all' | 'assigned'>('assigned');
    const [assignedLearners, setAssignedLearners] = useState<string[]>([]);
    const [availableLearners, setAvailableLearners] = useState<User[]>([]);
    const [authorizedApps, setAuthorizedApps] = useState<any>({});
    const [passwordMode, setPasswordMode] = useState<'define' | 'email'>('email');

    const fetchOrganizations = async () => {
        try {
            const res = await api.get('/api/organizations');
            setOrganizations(res.data);
        } catch (err) {
            console.error("Error fetching organizations", err);
        }
    };

    const fetchLearners = async (orgId: string) => {
        try {
            // Fetch users with role 'learner' for the selected organization
            // Note: The backend /api/users endpoint filters by organization for non-super-admins automatically.
            // For super-admins, we might need to rely on client-side filtering or backend support if the endpoint returns all.
            // Assuming /api/users returns all users the requester has access to.
            const res = await api.get('/api/users');
            const learners = res.data.filter((u: User) => u.role === 'learner' && (u.organizationId === orgId || (typeof u.organizationId === 'object' && u.organizationId?._id === orgId)));
            setAvailableLearners(learners);
        } catch (err) {
            console.error("Error fetching learners", err);
        }
    };

    useEffect(() => {
        if (currentUser?.role === 'super-admin') {
            fetchOrganizations();
        }
    }, [currentUser]);

    useEffect(() => {
        if (organizationId && role === 'trainer') {
            fetchLearners(organizationId);
        }
    }, [organizationId, role]);

    useEffect(() => {
        if (initialData && isEditing) {
            setFirstName(initialData.firstName);
            setLastName(initialData.lastName);
            setEmail(initialData.email);
            setRole(initialData.role);
            setOrganizationId(typeof initialData.organizationId === 'string' ? initialData.organizationId : (initialData.organizationId?._id || ''));
            setPassword(''); // Don't populate password
            setPasswordMode('define'); // Default to define when editing (usually to change it)

            // Populate new fields
            if (initialData.role === 'trainer' && initialData.trainerSettings) {
                setQuestionScope(initialData.trainerSettings.questionScope || 'assigned');
                setAssignedLearners(initialData.trainerSettings.assignedLearners || []);
            }
            if (initialData.role === 'of-admin' && initialData.authorizedApps) {
                setAuthorizedApps(initialData.authorizedApps);
            }
        } else {
            setFirstName('');
            setLastName('');
            setEmail('');
            setPassword('');
            setRole('learner');
            setOrganizationId('');
            setQuestionScope('assigned');
            setAssignedLearners([]);
            setAuthorizedApps({});
            setPasswordMode('email'); // Default to email for new users
        }
    }, [initialData, isEditing, open]);

    const handleSubmit = () => {
        const data: any = {
            firstName,
            lastName,
            email,
            role,
            organizationId: (currentUser?.role === 'super-admin' && organizationId) ? organizationId : undefined,
            sendPasswordEmail: !isEditing && passwordMode === 'email'
        };

        if (passwordMode === 'define' && password) {
            data.password = password;
        }

        if (role === 'trainer') {
            data.trainerSettings = {
                questionScope,
                assignedLearners
            };
        }

        if (role === 'of-admin') {
            data.authorizedApps = authorizedApps;
        }

        onSave(data);
        onClose();
    };

    const canAssignRole = (targetRole: string) => {
        if (currentUser?.role === 'super-admin') return true;
        if (currentUser?.role === 'of-admin') {
            return ['learner', 'trainer'].includes(targetRole);
        }
        return false;
    };

    const handleAppToggle = (appKey: string) => {
        setAuthorizedApps((prev: any) => ({
            ...prev,
            [appKey]: !prev[appKey]
        }));
    };

    const availableApps = [
        { key: 'cursus', label: 'Cursus', "aria-label": 'Cursus' },
        { key: 'courses', label: 'Cours', "aria-label": 'Cours' },
        { key: 'marketing', label: 'Marketing', "aria-label": 'Marketing' },
        { key: 'users', label: 'Utilisateurs', "aria-label": 'Utilisateurs' },
        { key: 'organizations', label: 'Organisations', "aria-label": 'Organisations' },
        { key: 'enrollment', label: 'Inscriptions', "aria-label": 'Inscriptions' },
        { key: 'learnerSpace', label: 'Espace Apprenant', "aria-label": 'Espace Apprenant' },
        { key: 'apprenants', label: 'Gestion Apprenants', "aria-label": 'Gestion Apprenants' },
        { key: 'block-evaluation', label: 'Évaluations Blocs', "aria-label": 'Évaluations Blocs' },
        { key: 'monof', label: 'Mon OF', "aria-label": 'Mon OF' },
        { key: 'reports', label: 'Rapports', "aria-label": 'Rapports' },
        { key: 'questions', label: 'Questions', "aria-label": 'Questions' },
        { key: 'trainers', label: 'Formateurs', "aria-label": 'Formateurs' },
        { key: 'statistics', label: 'Statistiques', "aria-label": 'Statistiques' },
        { key: 'satisfaction', label: 'Satisfaction', "aria-label": 'Satisfaction' },
        { key: 'media', label: 'Médiathèque', "aria-label": 'Médiathèque' },
        { key: 'contracts', label: 'Contrats', "aria-label": 'Contrats' },
        { key: 'error', label: 'Signalements', "aria-label": 'Signalements' }
    ];

    const handlePasswordModeChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: 'define' | 'email' | null,
    ) => {
        if (newMode !== null) {
            setPasswordMode(newMode);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Section: Rôle & Organisation */}
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Rôle & Accès
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: currentUser?.role === 'super-admin' && role !== 'super-admin' ? 6 : 12 }}>
                            <FormControl fullWidth>
                                <InputLabel>Rôle</InputLabel>
                                <Select
                                    value={role}
                                    label="Rôle"
                                    onChange={(e) => setRole(e.target.value)}
                                >
                                    <MenuItem value="learner">Apprenant</MenuItem>
                                    <MenuItem value="trainer">Formateur</MenuItem>
                                    {canAssignRole('of-admin') && <MenuItem value="of-admin">Responsable OF</MenuItem>}
                                    {canAssignRole('super-admin') && <MenuItem value="super-admin">Administrateur</MenuItem>}
                                </Select>
                                {currentUser?.role === 'of-admin' && (
                                    <FormHelperText>Vous ne pouvez créer que des Apprenants et des Formateurs.</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {currentUser?.role === 'super-admin' && role !== 'super-admin' && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Organisme</InputLabel>
                                    <Select
                                        value={organizationId}
                                        label="Organisme"
                                        onChange={(e) => setOrganizationId(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Aucun</em>
                                        </MenuItem>
                                        {organizations.map((org) => (
                                            <MenuItem key={org._id} value={org._id}>
                                                {org.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {/* Section: Informations Personnelles */}
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Informations Personnelles
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Prénom"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Nom"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {/* Section: Sécurité */}
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Sécurité
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        {!isEditing && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <ToggleButtonGroup
                                    value={passwordMode}
                                    exclusive
                                    onChange={handlePasswordModeChange}
                                    aria-label="Mode de mot de passe"
                                    sx={{ width: '100%' }}
                                >
                                    <ToggleButton
                                        value="define"
                                        aria-label="Définir le mot de passe"
                                        sx={{
                                            flex: 1,
                                            '&.Mui-selected': {
                                                bgcolor: '#ff9800',
                                                color: 'white',
                                                '&:hover': { bgcolor: '#f57c00' }
                                            }
                                        }}
                                    >
                                        Définir
                                    </ToggleButton>
                                    <ToggleButton
                                        value="email"
                                        aria-label="Envoyer par email"
                                        sx={{
                                            flex: 1,
                                            '&.Mui-selected': {
                                                bgcolor: '#9c27b0',
                                                color: 'white',
                                                '&:hover': { bgcolor: '#7b1fa2' }
                                            }
                                        }}
                                    >
                                        Via Mail
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}

                        {passwordMode === 'define' && (
                            <TextField
                                label={isEditing ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe"}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                required={!isEditing}
                            />
                        )}

                        {passwordMode === 'email' && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic', py: 1 }}>
                                L'utilisateur recevra un email contenant un lien pour définir son mot de passe.
                            </Typography>
                        )}
                    </Box>

                    {/* Section: Paramètres Spécifiques */}
                    {(role === 'trainer' || role === 'of-admin') && (
                        <>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Paramètres Spécifiques
                            </Typography>
                        </>
                    )}

                    {/* Trainer Specific Settings */}
                    {role === 'trainer' && (
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#f8f9fa' }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Portée des questions</InputLabel>
                                        <Select
                                            value={questionScope}
                                            label="Portée des questions"
                                            onChange={(e) => setQuestionScope(e.target.value as 'all' | 'assigned')}
                                        >
                                            <MenuItem value="all">Toutes les questions</MenuItem>
                                            <MenuItem value="assigned">Uniquement les apprenants assignés</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {organizationId && (
                                    <Grid size={{ xs: 12 }}>
                                        <FormControl fullWidth>
                                            <InputLabel>Apprenants assignés</InputLabel>
                                            <Select
                                                multiple
                                                value={assignedLearners}
                                                label="Apprenants assignés"
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setAssignedLearners(typeof value === 'string' ? value.split(',') : value);
                                                }}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const learner = availableLearners.find(l => l._id === value);
                                                            return (
                                                                <Chip
                                                                    key={value}
                                                                    label={learner ? `${learner.firstName} ${learner.lastName}` : value}
                                                                    size="small"
                                                                    sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            >
                                                {availableLearners.map((learner) => (
                                                    <MenuItem key={learner._id} value={learner._id}>
                                                        {learner.firstName} {learner.lastName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {/* OF Admin Specific Settings */}
                    {role === 'of-admin' && (
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#f8f9fa' }}>
                            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>Applications Autorisées :</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {availableApps.map((app) => (
                                    <Chip
                                        key={app.key}
                                        label={app.label}
                                        aria-label={app.label}
                                        onClick={() => handleAppToggle(app.key)}
                                        color={authorizedApps[app.key] ? "primary" : "default"}
                                        variant={authorizedApps[app.key] ? "filled" : "outlined"}
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { transform: 'translateY(-1px)' }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annuler</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserModal;
